import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Play, Trash2, Settings, CheckSquare, Loader2,
  GraduationCap, School, AlertTriangle,
  Trophy, Award, AlertOctagon, MessageSquare, RotateCcw,
  Search, Filter
} from "lucide-react";
import { apiService } from "@/services/api";
import { useAuthStore } from "@/store/authStore";
import { toast } from "sonner";
import { useLang } from "@/hooks/useLang";

const t = {
  fr: {
    title: "Conseil de classe automatique",
    subtitle: "Génération automatique des conseils de classe selon les paramètres configurés",
    school: "École",
    year: "Année scolaire",
    branch: "Branche",
    cycle: "Cycle",
    formation: "Formation",
    specialty: "Spécialité",
    level: "Niveau",
    class: "Classe",
    selectClasses: "Sélectionner les classes",
    selectAll: "Tout sélectionner",
    clearSelection: "Effacer la sélection",
    invertSelection: "Inverser la sélection",
    period: "Période",
    actions: "Actions à effectuer",
    generate: "Générer les conseils de classe",
    generating: "Génération en cours...",
    deleteAll: "Supprimer les conseils de classe",
    deleteWarning: "Attention, cette opération est irréversible",
    generalDecisions: "Décisions générales",
    honorRoll: "Générer les tableaux d'honneur, encouragements, félicitations",
    avertTravail: "Générer les avertissements/blâmes travail suivant la moyenne",
    avertConduite: "Générer les avertissements/blâmes conduite et jours d'exclusion",
    observations: "Générer les observations du conseil de classe",
    unranked: "Déterminer les élèves non classés",
    yearEndDecisions: "Décisions de fin d'année",
    generateExclusions: "Générer les exclusions définitives si paramètres trouvés",
    generatePromotions: "Générer les redoublements et passages en classe supérieure",
    tips: "Astuces",
    tip1: "Configurer les classes suivantes : Administration → Classes Suivantes",
    tip2: "Configurer les formules de calcul : Administration → Formules (TH, Moyenne, Rang)",
    tip3: "Programmer les décisions de fin d'année : Administration → Décision de fin d'année",
    tip4: "Saisir les notes des élèves : Académique → Examens → Saisie des notes",
    tip5: "Gérer la discipline/conduite : Académique → Examens → Discipline/Conduite",
    results: "Résultats",
    processed: "élèves traités",
    success: "Succès",
    error: "Erreur",
    noClassSelected: "Veuillez sélectionner au moins une classe",
    noActionSelected: "Veuillez sélectionner au moins une action",
    classCount: "classes sélectionnées",
    studentCount: "élèves",
    search: "Rechercher...",
    validationTitle: "Validation des résultats",
    classSummary: "Résumé par classe",
    avgScore: "Moyenne générale",
    distinctions: "Distinctions",
    decisions: "Décisions",
    openExpress: "Ouvrir le conseil express",
    validateAll: "Tout valider",
    backToForm: "Retour au formulaire",
    students: "élèves",
    workDistinctions: "Distinctions travail",
    conductDistinctions: "Distinctions conduite",
    noDetails: "Aucun détail disponible",
    classValidated: "Classe validée",
  },
  en: {
    title: "Automatic Class Council",
    subtitle: "Automatically generate class council results based on configured parameters",
    school: "School",
    year: "Academic Year",
    branch: "Branch",
    cycle: "Cycle",
    formation: "Formation",
    specialty: "Specialty",
    level: "Level",
    class: "Class",
    selectClasses: "Select classes",
    selectAll: "Select all",
    clearSelection: "Clear selection",
    invertSelection: "Invert selection",
    period: "Period",
    actions: "Actions to perform",
    generate: "Generate class councils",
    generating: "Generating...",
    deleteAll: "Delete class councils",
    deleteWarning: "Warning, this operation is irreversible",
    generalDecisions: "General decisions",
    honorRoll: "Generate honor roll, encouragements, congratulations",
    avertTravail: "Generate work warnings/blames based on average",
    avertConduite: "Generate conduct warnings/blames and exclusion days",
    observations: "Generate class council observations",
    unranked: "Determine unranked students",
    yearEndDecisions: "Year-end decisions",
    generateExclusions: "Generate definitive exclusions if parameters found",
    generatePromotions: "Generate repeats and promotions to upper class",
    tips: "Tips",
    tip1: "Configure next classes: Administration → Next Classes",
    tip2: "Configure calculation formulas: Administration → Formulas (TH, Average, Rank)",
    tip3: "Program year-end decisions: Administration → Year-End Decisions",
    tip4: "Enter student grades: Academic → Exams → Grade Entry",
    tip5: "Manage discipline/conduct: Academic → Exams → Discipline/Conduct",
    results: "Results",
    processed: "students processed",
    success: "Success",
    error: "Error",
    noClassSelected: "Please select at least one class",
    noActionSelected: "Please select at least one action",
    classCount: "classes selected",
    studentCount: "students",
    search: "Search...",
    validationTitle: "Results validation",
    classSummary: "Class summary",
    avgScore: "General average",
    distinctions: "Distinctions",
    decisions: "Decisions",
    openExpress: "Open express council",
    validateAll: "Validate all",
    backToForm: "Back to form",
    students: "students",
    workDistinctions: "Work distinctions",
    conductDistinctions: "Conduct distinctions",
    noDetails: "No details available",
    classValidated: "Class validated",
  }
};

export default function ClassCouncilAutoPage() {
  const { lang } = useLang();
  const txt = t[lang];
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [academicYears, setAcademicYears] = useState<any[]>([]);
  const [classes, setClasses] = useState<any[]>([]);
  const [periods, setPeriods] = useState<any[]>([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [selectedClasses, setSelectedClasses] = useState<Set<number>>(new Set());
  const [searchClass, setSearchClass] = useState("");
  const [result, setResult] = useState<any>(null);

  const [actions, setActions] = useState<Record<string, boolean>>({
    honor: true,
    encouragements: true,
    felicitations: true,
    avert_travail: true,
    blame_travail: true,
    avert_conduite: true,
    blame_conduite: true,
    observations: true,
    unranked: false,
    exclusions: true,
    promotions: true,
  });

  const schoolId = user?.school_id || 1;

  const loadData = useCallback(async () => {
    if (!schoolId) return;
    try {
      // Années scolaires — endpoint: /schools/{schoolId}/academic-years
      let years: any[] = [];
      try {
        const yearRes = await apiService.get(`/schools/${schoolId}/academic-years`);
        console.log("[Auto] years response:", yearRes);
        years = Array.isArray(yearRes) ? yearRes : (yearRes as any)?.data || (yearRes as any)?.items || [];
        console.log("[Auto] years loaded:", years.length);
      } catch (e: any) {
        console.error("[Auto] years error:", e?.response?.data || e?.message || e);
      }
      setAcademicYears(years);

      // Classes — endpoint: /classes?school_id=...
      let cls: any[] = [];
      try {
        const clsRes = await apiService.get("/classes", { params: { school_id: schoolId, per_page: 1000 } });
        console.log("[Auto] classes response:", clsRes);
        cls = Array.isArray(clsRes) ? clsRes : (clsRes as any)?.data || (clsRes as any)?.items || [];
        console.log("[Auto] classes loaded:", cls.length);
      } catch (e: any) {
        console.error("[Auto] classes error:", e?.response?.data || e?.message || e);
      }
      setClasses(cls);

      // Périodes — endpoint: /grades/periods
      let per: any[] = [];
      try {
        const perRes = await apiService.get("/grades/periods", { params: { school_id: schoolId } });
        console.log("[Auto] periods response:", perRes);
        per = Array.isArray(perRes) ? perRes : (perRes as any)?.data || (perRes as any)?.items || [];
        console.log("[Auto] periods loaded:", per.length);
      } catch (e: any) {
        console.error("[Auto] periods error:", e?.response?.data || e?.message || e);
      }
      setPeriods(per);
    } catch (e) {
      toast.error(txt.error);
    }
  }, [schoolId, txt.error]);

  useEffect(() => { loadData(); }, [loadData]);

  const filteredClasses = useMemo(() => {
    let list = classes;
    if (searchClass) {
      const q = searchClass.toLowerCase();
      list = list.filter(c => (c.name || "").toLowerCase().includes(q));
    }
    return list;
  }, [classes, searchClass]);

  const toggleClass = (id: number) => {
    setSelectedClasses(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelectedClasses(new Set(filteredClasses.map(c => c.id)));
  const clearAll = () => setSelectedClasses(new Set());
  const invertSelection = () => {
    setSelectedClasses(prev => {
      const next = new Set<number>();
      filteredClasses.forEach(c => { if (!prev.has(c.id)) next.add(c.id); });
      return next;
    });
  };

  const toggleAction = (key: string) => {
    setActions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const selectedActions = useMemo(() =>
    Object.entries(actions).filter(([,v]) => v).map(([k]) => k),
  [actions]);

  const handleGenerate = async () => {
    if (selectedClasses.size === 0) { toast.error(txt.noClassSelected); return; }
    if (selectedActions.length === 0) { toast.error(txt.noActionSelected); return; }
    if (!selectedYear) { toast.error(txt.year); return; }

    setLoading(true);
    try {
      const res = await apiService.post("/class-council/generate", {
        school_id: schoolId,
        academic_year_id: parseInt(selectedYear),
        class_ids: Array.from(selectedClasses),
        evaluation_period_id: selectedPeriod ? parseInt(selectedPeriod) : null,
        is_annual: !selectedPeriod,
        actions: selectedActions,
      });
      setResult(res);
      toast.success(`${(res as any)?.message || txt.success}`);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || txt.error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedYear) { toast.error(txt.year); return; }
    if (!confirm(txt.deleteWarning)) return;
    try {
      await apiService.delete("/class-council/results", {
        params: { academic_year_id: parseInt(selectedYear), class_ids: selectedClasses.size > 0 ? Array.from(selectedClasses) : undefined }
      });
      toast.success(txt.success);
      setResult(null);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || txt.error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <GraduationCap className="w-7 h-7 text-[#1e3a8a]" /> {txt.title}
        </h1>
        <p className="text-sm text-gray-500 mt-1">{txt.subtitle}</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-1 space-y-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4"><Filter className="w-4 h-4 text-[#1e3a8a]" /> Filtres</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{txt.year} *</label>
                {academicYears.length > 0 ? (
                  <select value={selectedYear} onChange={e => setSelectedYear(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]">
                    <option value="">{lang === 'fr' ? 'Sélectionner...' : 'Select...'}</option>
                    {academicYears.map(y => <option key={y.id} value={String(y.id)}>{y.name || y.label || `Année ${y.id}`}</option>)}
                  </select>
                ) : (
                  <input type="number" value={selectedYear} onChange={e => setSelectedYear(e.target.value)} placeholder="ID année (ex: 1)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]" />
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{txt.period}</label>
                {periods.length > 0 ? (
                  <select value={selectedPeriod} onChange={e => setSelectedPeriod(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]">
                    <option value="">{lang === 'fr' ? "Annuelle (toute l'année)" : "Annual (whole year)"}</option>
                    {periods.map(p => <option key={p.id} value={String(p.id)}>{p.label_fr || p.label || p.code || `Période ${p.id}`}</option>)}
                  </select>
                ) : (
                  <input type="number" value={selectedPeriod} onChange={e => setSelectedPeriod(e.target.value)} placeholder="ID période (optionnel)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]" />
                )}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-3"><School className="w-4 h-4 text-[#1e3a8a]" /> {txt.selectClasses}</h3>
            {classes.length > 0 ? (
              <>
                <div className="relative mb-3">
                  <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input type="text" placeholder={txt.search} value={searchClass} onChange={e => setSearchClass(e.target.value)} className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm w-full" />
                </div>
                <div className="flex gap-2 mb-3">
                  <button onClick={selectAll} className="px-2 py-1 bg-gray-100 rounded text-xs hover:bg-gray-200">{txt.selectAll}</button>
                  <button onClick={clearAll} className="px-2 py-1 bg-gray-100 rounded text-xs hover:bg-gray-200">{txt.clearSelection}</button>
                  <button onClick={invertSelection} className="px-2 py-1 bg-gray-100 rounded text-xs hover:bg-gray-200">{txt.invertSelection}</button>
                </div>
                <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
                  {filteredClasses.map(c => (
                    <label key={c.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-0">
                      <input type="checkbox" checked={selectedClasses.has(c.id)} onChange={() => toggleClass(c.id)} className="w-4 h-4 text-[#1e3a8a] rounded" />
                      <span className="text-sm text-gray-700">{c.name}</span>
                      <span className="text-xs text-gray-400 ml-auto">{c.level_name || c.levelId}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">{selectedClasses.size} {txt.classCount} — {filteredClasses.reduce((sum, c) => selectedClasses.has(c.id) ? sum + (c.student_count || 0) : sum, 0)} {txt.studentCount}</p>
              </>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-amber-600">{lang === 'fr' ? 'Aucune classe chargée. Saisissez les IDs manuellement:' : 'No classes loaded. Enter IDs manually:'}</p>
                <input type="text" value={Array.from(selectedClasses).join(",")} onChange={e => {
                  const ids = e.target.value.split(",").map(s => parseInt(s.trim())).filter(n => !isNaN(n));
                  setSelectedClasses(new Set(ids));
                }} placeholder="1,2,3" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
            )}
          </div>

          <div className="bg-amber-50 rounded-xl border border-amber-200 p-4">
            <h3 className="text-sm font-semibold text-amber-800 flex items-center gap-2 mb-2"><AlertTriangle className="w-4 h-4" /> {txt.tips}</h3>
            <ul className="space-y-1 text-xs text-amber-700">
              <li>• {txt.tip1}</li><li>• {txt.tip2}</li><li>• {txt.tip3}</li><li>• {txt.tip4}</li><li>• {txt.tip5}</li>
            </ul>
          </div>
        </div>

        <div className="xl:col-span-2 space-y-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4"><Settings className="w-4 h-4 text-[#1e3a8a]" /> {txt.actions}</h3>
            <div className="space-y-4">
              <div>
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">{txt.generalDecisions}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {[
                    { key: "honor", label: txt.honorRoll, icon: Trophy },
                    { key: "avert_travail", label: txt.avertTravail, icon: AlertOctagon },
                    { key: "avert_conduite", label: txt.avertConduite, icon: AlertTriangle },
                    { key: "observations", label: txt.observations, icon: MessageSquare },
                    { key: "unranked", label: txt.unranked, icon: RotateCcw },
                  ].map(({ key, label, icon: Icon }) => (
                    <label key={key} className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                      <input type="checkbox" checked={actions[key]} onChange={() => toggleAction(key)} className="w-4 h-4 text-[#1e3a8a] rounded" />
                      <Icon className="w-4 h-4 text-gray-500" /><span className="text-sm text-gray-700">{label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">{txt.yearEndDecisions}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {[
                    { key: "exclusions", label: txt.generateExclusions, icon: AlertTriangle },
                    { key: "promotions", label: txt.generatePromotions, icon: GraduationCap },
                  ].map(({ key, label, icon: Icon }) => (
                    <label key={key} className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                      <input type="checkbox" checked={actions[key]} onChange={() => toggleAction(key)} className="w-4 h-4 text-[#1e3a8a] rounded" />
                      <Icon className="w-4 h-4 text-gray-500" /><span className="text-sm text-gray-700">{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-6 pt-4 border-t border-gray-200">
              <button onClick={handleGenerate} disabled={loading || selectedClasses.size === 0} className="px-6 py-2.5 bg-[#1e3a8a] text-white rounded-lg text-sm font-medium hover:bg-[#152a5e] disabled:opacity-50 flex items-center gap-2">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                {loading ? txt.generating : txt.generate}
              </button>
              <button onClick={handleDelete} className="px-6 py-2.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 flex items-center gap-2">
                <Trash2 className="w-4 h-4" /> {txt.deleteAll}
              </button>
            </div>
          </div>

          {result && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
                <CheckSquare className="w-5 h-5 text-emerald-600" /> {txt.validationTitle}
              </h3>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
                <p className="text-emerald-800 font-medium text-lg">{(result as any)?.message}</p>
                <p className="text-emerald-600 text-sm mt-1">{(result as any)?.processed} {txt.processed}</p>
              </div>

              {(result as any)?.details && (result as any).details.length > 0 ? (
                <div className="space-y-4">
                  <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wider">{txt.classSummary}</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{txt.class}</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">{txt.studentCount}</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">{txt.avgScore}</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase text-amber-700">TH</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase text-blue-700">Enc.</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase text-cyan-700">Félic.</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase text-orange-700">Avert.T</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase text-red-700">Blâme T</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase text-orange-600">Avert.C</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase text-red-600">Blâme C</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase text-emerald-700">Admis</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase text-amber-700">Redoubl.</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase text-red-700">Exclu</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">{txt.actions}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {(result as any).details.map((d: any, i: number) => (
                          <tr key={i} className="hover:bg-gray-50/50 transition-colors">
                            <td className="px-4 py-3 font-medium text-gray-900">
                              <div className="flex items-center gap-2">
                                <School className="w-4 h-4 text-[#1e3a8a]" />
                                {d.class_name || `Classe ${d.class_id}`}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center font-bold text-gray-900">{d.processed}</td>
                            <td className="px-4 py-3 text-center font-bold text-[#1e3a8a]">{d.average_score > 0 ? d.average_score.toFixed(2) : '-'}</td>
                            <td className="px-4 py-3 text-center">{d.tableau_honneur > 0 ? <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-bold">{d.tableau_honneur}</span> : '-'}</td>
                            <td className="px-4 py-3 text-center">{d.encouragements > 0 ? <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-bold">{d.encouragements}</span> : '-'}</td>
                            <td className="px-4 py-3 text-center">{d.felicitations > 0 ? <span className="px-2 py-0.5 bg-cyan-100 text-cyan-700 rounded-full text-xs font-bold">{d.felicitations}</span> : '-'}</td>
                            <td className="px-4 py-3 text-center">{d.avertissement_travail > 0 ? <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-xs font-bold">{d.avertissement_travail}</span> : '-'}</td>
                            <td className="px-4 py-3 text-center">{d.blame_travail > 0 ? <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-bold">{d.blame_travail}</span> : '-'}</td>
                            <td className="px-4 py-3 text-center">{d.avertissement_conduite > 0 ? <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-xs font-bold">{d.avertissement_conduite}</span> : '-'}</td>
                            <td className="px-4 py-3 text-center">{d.blame_conduite > 0 ? <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-bold">{d.blame_conduite}</span> : '-'}</td>
                            <td className="px-4 py-3 text-center">{d.admis > 0 ? <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-bold">{d.admis}</span> : '-'}</td>
                            <td className="px-4 py-3 text-center">{d.redouble > 0 ? <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-bold">{d.redouble}</span> : '-'}</td>
                            <td className="px-4 py-3 text-center">{d.exclu > 0 ? <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-bold">{d.exclu}</span> : '-'}</td>
                            <td className="px-4 py-3 text-center">
                              <button
                                onClick={() => navigate(`/class-council/express/${d.class_id}`)}
                                className="px-3 py-1.5 bg-[#1e3a8a] text-white rounded-lg text-xs font-medium hover:bg-[#152a5e] transition-colors flex items-center gap-1"
                              >
                                <GraduationCap className="w-3 h-3" />
                                {txt.openExpress}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex gap-3 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => setResult(null)}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 flex items-center gap-2"
                    >
                      <RotateCcw className="w-4 h-4" /> {txt.backToForm}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
                  {(result as any).details?.map((d: any, i: number) => (
                    <div key={i} className="bg-gray-50 rounded-lg p-2 text-center border border-gray-200">
                      <p className="text-xs text-gray-500">Classe {d.class_id}</p>
                      <p className="text-lg font-bold text-[#1e3a8a]">{d.processed}</p>
                      <p className="text-[10px] text-gray-400">élèves</p>
                    </div>
                  )) || <p className="text-sm text-gray-400">{txt.noDetails}</p>}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}